import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors duration-200"
      >
        <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span>{article.readTime} 分钟阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
      >
        <div className="aspect-[16/10] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-5">
          <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700 mb-3 whitespace-nowrap">
            {article.category}
          </span>
          <h3 className="text-lg font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200 mb-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2 mb-4">
            {article.excerpt}
          </p>
          <div className="flex items-center justify-between text-xs text-foreground-500">
            <span>{article.date}</span>
            <span>{article.readTime} 分钟阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group flex flex-col sm:flex-row gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
    >
      <div className="w-full sm:w-48 h-36 rounded-lg overflow-hidden flex-shrink-0">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
              {article.category}
            </span>
            <span className="text-xs text-foreground-500">{article.date}</span>
          </div>
          <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200 mb-1.5">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2">
            {article.excerpt}
          </p>
        </div>
        <div className="flex items-center gap-2 mt-3 text-xs text-foreground-500">
          <span>{article.readTime} 分钟阅读</span>
          <span>·</span>
          <span>{article.author}</span>
        </div>
      </div>
    </Link>
  );
}